import React from 'react';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { calcNumUTCDays } from 'shared/src/utils/date/date-utils';
import { toUTCDate } from 'shared/src/date-string-utils/date-utils';
import { useExecutionContext } from '@/features/executions/execution-context';
import {
  formatCurrSafe,
  formatDateSafe,
  formatPctSafe,
  NumDays,
  shouldShowAgency,
  shouldShowRefRate,
  ViewField
} from '@/features/proposals/strategy-form-utils';
import { CampaignHeroMetrics } from '@/features/campaigns/campaign-hero-metrics';
import { trpc } from '@/utils/trpc-client';
import { isReady } from 'shared/src/utils/zod/trpc-zod-utils';

export function ExecutionTopSection() {
  return (
    <div className="w-full">
      <div className="border border-gray-100 bg-gray-100 rounded-t-lg p-4">
        <ExecutionAttributes />
      </div>
      <StrategyMetrics />
    </div>
  );
}

function StrategyMetrics() {
  const { execution } = useExecutionContext();
  const lineItems = trpc.getExecutionLineItems.useQuery({
    execution_id: execution.id
  });
  const metrics = trpc.campaignMetrics.useQuery({ campaign_id: execution.campaign_id });
  if (!isReady(lineItems) || !isReady(metrics)) return <div>Loading...</div>;

  return (
    <>
      <CampaignHeroMetrics metrics={metrics.data} />
    </>
  );
}

function ExecutionAttributes() {
  const { campaign } = useCampaignContext();
  const { execution } = useExecutionContext();
  const { start_date, end_date } = execution;
  const numDays =
    start_date && end_date ? calcNumUTCDays(toUTCDate(start_date), toUTCDate(end_date)) : '-';
  const showRefRate = shouldShowRefRate(execution, campaign);
  const showAgency = shouldShowAgency(execution, campaign);

  return (
    <div className="flex flex-col gap-1 bg-gray-100">
      <div className="flex items-center justify-between gap-1">
        <div className="pl-1 text-sm font-semibold text-gray-600">Campaign Performance</div>
        <div className="flex gap-1">
          <ViewField label="Price" value={formatCurrSafe(execution.price)} />
          <ViewField label="Start" value={formatDateSafe(execution.start_date)} />
          <ViewField label="End" value={formatDateSafe(execution.end_date)} />
          <NumDays numDays={numDays} />
        </div>
      </div>
      {showRefRate && (
        <div className="flex w-full items-center justify-end">
          <div className="flex gap-1">
            <ViewField
              label="Ref. Rate"
              value={formatPctSafe(execution.referral_commission_rate)}
            />
            <ViewField label="Eff. Price" value={formatCurrSafe(execution.effective_price)} />
            <ViewField
              label="Ref. Fee"
              value={formatCurrSafe(execution.referral_commission_amount)}
            />
          </div>
        </div>
      )}
      {showAgency && (
        <div className="flex w-full items-center justify-end">
          <div className="flex gap-1">
            <ViewField label="Agency %" value={formatPctSafe(execution.agency_markup_rate)} />
            <ViewField label="Markup Amt." value={formatCurrSafe(execution.agency_markup_amount)} />
            <ViewField label="Gross Price" value={formatCurrSafe(execution.gross_price)} />
          </div>
        </div>
      )}
    </div>
  );
}
